import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, Eye, FileText, Download, XCircle, Trash2 } from "lucide-react";
import WhatsAppShareButton from "../../components/WhatsAppShareButton";
import { toast, Toaster } from "sonner";

export default function AccountsInvoices() {
    const { user } = useAuth();
    const canDelete = user && (user.role === "admin" || user.role === "editor");
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const load = () => api.get("/invoices", { params: { q: q || undefined } }).then(r => setItems(r.data));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const cancelInvoice = async (inv) => {
        if (!window.confirm(`Cancel invoice ${inv.invoice_number}?\n\nThis VOIDS the invoice — it stops counting in Sales Register, Day Book, P&L and GST returns. You cannot un-cancel it. The PDF stays available for record-keeping.`)) return;
        try {
            await api.post(`/invoices/${inv.id}/cancel`);
            toast.success(`Invoice ${inv.invoice_number} cancelled`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cancel failed");
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/invoices/${pendingDelete.id}`);
            toast.success(`Invoice ${pendingDelete.invoice_number} deleted (logged to activity)`);
            setPendingDelete(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="accounts-invoices" className="space-y-4">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-eyebrow text-sky-700">Billing</div>
                    <h1 className="font-serif text-4xl text-stone-900">Invoices</h1>
                </div>
                <Link to="/employee/accounts/invoices/new"><Button className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="acc-new-invoice"><Plus className="w-4 h-4 mr-2" />New invoice</Button></Link>
            </div>

            <div className="bg-white border border-stone-200 p-3 flex gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by number/customer/phone…" className="rounded-none border-stone-300" />
                <Button onClick={load} className="rounded-none">Search</Button>
            </div>

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="border-b border-stone-200 bg-stone-50">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Number</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Date</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Total</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Outstanding</th>
                        <th className="p-3"></th>
                    </tr></thead>
                    <tbody>
                        {items.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-stone-500">No invoices yet.</td></tr> :
                            items.map(i => {
                                const cancelled = i.status === "cancelled";
                                return (
                                    <tr key={i.id} className={`border-b border-stone-100 ${cancelled ? "opacity-60" : ""}`} data-testid={`acc-inv-${i.invoice_number}`}>
                                        <td className="p-3 font-mono">
                                            {cancelled && <span className="line-through text-stone-400">{i.invoice_number}</span>}
                                            {!cancelled && i.invoice_number}
                                        </td>
                                        <td className="p-3 text-stone-500">{i.created_at?.slice(0, 10)}</td>
                                        <td className="p-3">{i.customer.name} <span className="text-xs text-stone-400 uppercase">{i.customer.customer_type}</span></td>
                                        <td className="p-3">
                                            {cancelled
                                                ? <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 px-2 py-0.5 border border-red-200" data-testid={`acc-inv-status-${i.invoice_number}`}>Cancelled</span>
                                                : <span className="text-[10px] uppercase tracking-widest text-emerald-700">Active</span>}
                                        </td>
                                        <td className="p-3 text-right">{formatINR(i.total)}</td>
                                        <td className="p-3 text-right font-medium text-amber-700">{cancelled ? "—" : formatINR(i.outstanding || 0)}</td>
                                        <td className="p-3 text-right space-x-2 whitespace-nowrap">
                                            <Link to={`/invoice/${i.id}`} target="_blank" className="text-sky-700 inline-flex items-center text-xs"><Eye className="w-3.5 h-3.5 mr-1" />View</Link>
                                            <a href={`${(api.defaults.baseURL || "")}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="text-stone-700 inline-flex items-center text-xs" data-testid={`acc-inv-pdf-${i.invoice_number}`}><Download className="w-3.5 h-3.5 mr-1" />PDF</a>
                                            <WhatsAppShareButton invoice={i} variant="ghost" iconOnly={false} label="WhatsApp" testId={`acc-inv-wa-${i.invoice_number}`} />
                                            <Link to={`/employee/accounts/invoices/${i.id}`} className="text-stone-700 inline-flex items-center text-xs"><FileText className="w-3.5 h-3.5 mr-1" />Edit</Link>
                                            {!cancelled && (
                                                <button
                                                    onClick={() => cancelInvoice(i)}
                                                    className="text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-2 py-1 transition-colors inline-flex items-center text-xs"
                                                    title="Cancel / void invoice (keeps audit trail)"
                                                    data-testid={`acc-inv-cancel-${i.invoice_number}`}
                                                >
                                                    <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() => setPendingDelete(i)}
                                                    className="text-red-700 hover:text-white hover:bg-red-700 border border-red-300 hover:border-red-700 px-2 py-1 transition-colors inline-flex items-center text-xs"
                                                    title="Hard-delete invoice (admin/editor only)"
                                                    data-testid={`acc-inv-delete-${i.invoice_number}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
                <AlertDialogContent className="rounded-none" data-testid="acc-inv-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl text-red-700">Hard-delete invoice {pendingDelete?.invoice_number}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently removes <strong>{pendingDelete?.invoice_number}</strong> for <strong>{pendingDelete?.customer?.name || "—"}</strong> ({formatINR(pendingDelete?.total || 0)}).
                            Any payments recorded against it are removed and the linked order (if any) resets to <em>pending</em>.
                            <br /><br />
                            <span className="text-amber-700">⚠ For production write-offs prefer <em>Cancel</em> — it keeps the audit trail. Use Delete only for test data, duplicates, or mistakes.</span>
                            <br /><br />
                            This action is recorded in the Activity Log with your name and email.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="acc-inv-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="rounded-none bg-red-700 hover:bg-red-800 text-stone-50"
                            data-testid="acc-inv-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete permanently"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
